import { Capacitor, registerPlugin } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import consola from 'consola'

// Регистрируем кастомный плагин FirebaseTokenPlugin (только для iOS)
const FirebaseTokenPlugin = registerPlugin<any>('FirebaseTokenPlugin');

/**
 * Плагин для инициализации push-уведомлений на iOS
 * Работает только в iOS приложении
 * Разрешения запрашиваются ТОЛЬКО после авторизации и согласия пользователя
 */
export default defineNuxtPlugin(async () => {
  const logPrefix = '[PUSH-NOTIFICATIONS-iOS]';
  const log = (...args: any[]) => consola.log(logPrefix, ...args);

  // Работаем только на iOS
  if (!process.client || Capacitor.getPlatform() !== 'ios') {
    return;
  }

  log('iOS push notifications plugin loaded, waiting for user authentication...');

  // Флаг для предотвращения повторной инициализации
  let isInitialized = false;
  
  // Хэндлы слушателей ЭТОГО плагина — чтобы снимать только их.
  // Обработчика тапа здесь нет намеренно: он один на обе платформы и живёт
  // в layouts/default.vue (иначе действие выполнялось дважды).
  let registrationListener: any = null;
  let errorListener: any = null;
  let receivedListener: any = null;
  
  // Слушаем событие Firebase FCM токена (отправляется из AppDelegate)
  // Обрабатываем ВСЕГДА, независимо от isInitialized, чтобы не пропустить токен при повторном включении
  if (typeof window !== 'undefined') {
    window.addEventListener('firebaseFCMToken', async (event: any) => {
      const token = event?.detail?.token || event?.token;
      if (token) {
        log('📱 [FIREBASE EVENT] Received Firebase FCM token from native:', token.slice(0, 30) + '...', 'isInitialized:', isInitialized);
        
        try {
          // Сохраняем токен в storage
          const storageModule = await import('@features/notifications');
          const storage = storageModule.usePushNotificationStorage();
          await storage.setPushToken(token);
          log('✅ [FIREBASE EVENT] Token saved to local storage');

          // Отправляем токен на сервер только если плагин инициализирован (чтобы не отправлять при старте приложения)
          if (isInitialized) {
            const apiProfile = await import('~/entities/user/api');
            const tokenData = {
              token,
              platform: 'ios',
            };
            
            log('📤 [FIREBASE EVENT] Sending token to server:', {
              tokenPreview: token.slice(0, 30) + '...',
              platform: tokenData.platform,
            });

            await apiProfile.default.addPushToken(tokenData);
            log('✅ [FIREBASE EVENT] Token saved to server successfully');
          } else {
            log('⚠️ [FIREBASE EVENT] Token received but plugin not initialized yet - token saved to storage, will be sent after initialization');
          }
        } catch (error) {
          consola.error(logPrefix, '[FIREBASE EVENT] Error saving Firebase token:', error);
        }
      }
    });
  }

  /**
   * Снимает слушатели, которые повесил ЭТОТ плагин.
   *
   * Раньше здесь вызывался `removeAllListeners()`, который убивал и обработчик тапа по
   * уведомлению из layouts/default.vue — единственный на обе платформы. Инициализация
   * происходит ПОСЛЕ маунта лейаута (её дёргает вотчер на профиль при каждом холодном
   * старте у залогиненного гостя), поэтому тап переставал работать вовсе.
   */
  const removeAllPushListeners = async () => {
    log('Removing own push notification listeners...');
    try {
      const handles = [registrationListener, errorListener, receivedListener].filter(Boolean);
      registrationListener = null;
      errorListener = null;
      receivedListener = null;

      await Promise.all(handles.map((handle: any) => handle.remove()));
      log('Own push notification listeners removed');
    } catch (error) {
      consola.error(logPrefix, 'Error removing listeners:', error);
    }
  };

  /**
   * Удаляет FCM токен из Firebase (полностью)
   */
  const deleteFCMToken = async () => {
    log('🗑️ Attempting to delete FCM token from Firebase...');
    
    // Проверяем доступность плагина
    if (!Capacitor.isPluginAvailable('FirebaseTokenPlugin')) {
      log('⚠️ FirebaseTokenPlugin not available - plugin may not be registered in Xcode');
      return false;
    }
    
    try {
      await FirebaseTokenPlugin.deleteToken();
      log('✅ FCM token deleted from Firebase successfully');
      return true;
    } catch (error) {
      consola.error(logPrefix, '❌ Error deleting FCM token:', error);
      return false;
    }
  };

  /**
   * Получает текущий FCM токен из Firebase
   */
  const _getFCMToken = async (): Promise<string | null> => {
    log('📱 Attempting to get FCM token from Firebase...');
    
    // Проверяем доступность плагина
    if (!Capacitor.isPluginAvailable('FirebaseTokenPlugin')) {
      log('⚠️ FirebaseTokenPlugin not available - plugin may not be registered in Xcode');
      return null;
    }
    
    try {
      const result = await FirebaseTokenPlugin.getToken();
      if (result?.token) {
        log('✅ FCM token retrieved:', result.token.slice(0, 30) + '...');
        return result.token;
      }
      log('⚠️ No FCM token available');
      return null;
    } catch (error) {
      consola.error(logPrefix, '❌ Error getting FCM token:', error);
      return null;
    }
  };

  /**
   * Настраивает обработчики событий push notifications
   */
  const setupPushListeners = async () => {
    log('Setting up push notification listeners...');

    // Registration listener - получение токена от PushNotifications.register()
    // Срабатывает когда токен получен или обновлен
    registrationListener = await PushNotifications.addListener('registration', async (token) => {
      log('📱 [REGISTRATION EVENT] Push registration successful, token:', token.value.slice(0, 20) + '...');

      try {
        // Сохраняем токен в storage
        const storageModule = await import('@features/notifications');
        const storage = storageModule.usePushNotificationStorage();
        await storage.setPushToken(token.value);
        log('✅ Token saved to local storage');

        // Отправляем токен на сервер (всегда отправляем, даже если плагин не инициализирован)
        const apiProfile = await import('~/entities/user/api');
        const tokenData = {
          token: token.value,
          platform: 'ios',
        };
        
        log('📤 [REGISTRATION EVENT] Sending token to server:', {
          tokenPreview: token.value.slice(0, 30) + '...',
          platform: tokenData.platform,
        });

        await apiProfile.default.addPushToken(tokenData);
        log('✅ [REGISTRATION EVENT] Token saved to server successfully');
      } catch (error) {
        consola.error(logPrefix, '[REGISTRATION EVENT] Error saving token:', error);
      }
    });

    // Registration error listener
    errorListener = await PushNotifications.addListener('registrationError', (error) => {
      consola.error(logPrefix, 'Push registration error:', error);
    });

    // Push notification received (foreground)
    receivedListener = await PushNotifications.addListener('pushNotificationReceived', (notification) => {
      log('Push notification received:', notification.title);
      // Можно добавить кастомную обработку уведомлений
    });

    // ⚠️ Тап по уведомлению (`pushNotificationActionPerformed`) здесь НЕ обрабатываем.
    // Слушатель на это событие уже висит в layouts/default.vue и работает на обеих
    // платформах. Раньше обработчик был и тут, и там — на iOS один тап выполнял
    // действие ДВАЖДЫ: `quick_reorder` дублировал товары в корзине, навигация
    // срабатывала два раза. Единственная точка обработки — лейаут.

    log('Push notification listeners set up successfully');
  };

  /**
   * Глобальная функция для сброса состояния
   */
  globalThis.resetPushNotificationsState = async () => {
    log('Resetting push notifications state...');
    isInitialized = false;
    await removeAllPushListeners();
    
    // Удаляем FCM токен из Firebase (чтобы при повторном включении сгенерировался новый)
    const deleted = await deleteFCMToken();
    if (deleted) {
      log('✅ FCM token deleted from Firebase');
    } else {
      log('⚠️ Failed to delete FCM token from Firebase (will try to reuse existing on re-enable)');
    }
    
    log('Push notifications state reset complete');
  };

  /**
   * Глобальная функция для получения состояния (отладка)
   */
  globalThis.getPushNotificationsState = () => {
    return {
      isInitialized,
      platform: 'ios',
      isNativePlatform: true,
      hasListeners: registrationListener !== null,
    };
  };

  /**
   * Глобальная функция для инициализации push notifications после авторизации
   * Вызывается из модального окна при нажатии "Включить уведомления"
   */
  globalThis.initPushNotificationsAfterAuth = async () => {
    if (isInitialized) {
      log('Push notifications already initialized, skipping...');
      return true;
    }

    log('Initializing push notifications after authentication...');

    try {
      // 1. Проверяем текущий статус разрешений
      const permissionStatus = await PushNotifications.checkPermissions();
      log('Current permission status:', permissionStatus.receive);

      // 2. Если разрешения еще не запрашивались - запрашиваем
      if (permissionStatus.receive === 'prompt' || permissionStatus.receive === 'prompt-with-rationale') {
        log('Requesting push notification permissions...');
        const requestResult = await PushNotifications.requestPermissions();
        log('Permission request result:', requestResult.receive);

        if (requestResult.receive !== 'granted') {
          log('Push notification permissions denied by user');
          return false;
        }
      } else if (permissionStatus.receive === 'denied') {
        log('Push notification permissions are denied. User needs to enable them in Settings.');
        return false;
      }

      // 3. Удаляем старые слушатели (если были)
      await removeAllPushListeners();

      // 4. Настраиваем новые слушатели
      await setupPushListeners();

      // 5. Устанавливаем флаг ПЕРЕД регистрацией, чтобы Firebase событие могло обработаться
      isInitialized = true;
      
      // 6. Если разрешение уже было получено ранее, проверяем наличие сохраненного токена
      if (permissionStatus.receive === 'granted') {
        log('Permission already granted, checking for existing token...');
        try {
          const storageModule = await import('@features/notifications');
          const storage = storageModule.usePushNotificationStorage();
          const existingToken = await storage.getPushToken();
          
          if (existingToken) {
            log('Found existing token in storage:', existingToken.slice(0, 20) + '...');
            log('Re-sending token to server...');
            
            const apiProfile = await import('~/entities/user/api');
            const tokenData = {
              token: existingToken,
              platform: 'ios',
            };
            
            await apiProfile.default.addPushToken(tokenData);
            log('✅ Existing token re-sent to server successfully');
          } else {
            log('⚠️ No existing token found in storage');
            log('🔄 Will wait for token via registration event (from PushNotifications.register())');
            log('🔄 OR via firebaseFCMToken event (from AppDelegate)');
            // Токен будет получен через событие 'registration' от PushNotifications.register()
            // или через событие 'firebaseFCMToken' от AppDelegate
            // Оба события обработают токен и отправят на сервер
          }
        } catch (error) {
          log('Error checking existing token:', error);
        }
      }
      
      // 7. Регистрируем push-уведомления
      // Событие 'registration' сработает если токен будет получен/обновлен
      // Событие 'firebaseFCMToken' также может сработать если Firebase отправит токен
      log('Registering for push notifications...');
      
      // Проверяем, был ли токен до регистрации
      const storageModule = await import('@features/notifications');
      const storage = storageModule.usePushNotificationStorage();
      const tokenBeforeRegister = await storage.getPushToken();
      
      await PushNotifications.register();
      log('✅ PushNotifications.register() called - waiting for registration/Firebase events');
      
      // Если токена не было, даем время событиям сработать (особенно важно при повторном включении)
      if (!tokenBeforeRegister) {
        log('⏳ No token before registration, waiting for events (15s timeout)...');
        log('ℹ️ На iOS Firebase может кэшировать токен и отправить его с задержкой');
        
        // Проверяем токен каждую секунду в течение 15 секунд
        let tokenReceived = false;
        for (let i = 0; i < 15; i++) {
          await new Promise(resolve => setTimeout(resolve, 1000)); // Ждем 1 секунду
          const currentToken = await storage.getPushToken();
          if (currentToken) {
            log(`✅ Token received after ${i + 1}s:`, currentToken.slice(0, 20) + '...');
            tokenReceived = true;
            break;
          }
        }
        
        // Если токен не пришел через события за 15 секунд
        if (!tokenReceived) {
          log('⚠️ Token not received after 15s wait via events');
          log('ℹ️ Firebase кэширует токен на iOS. Он придет автоматически при следующем событии:');
          log('   - При перезапуске приложения');
          log('   - При обновлении токена Firebase');
          log('   - Через несколько минут (Firebase периодически проверяет токен)');
          log('ℹ️ Token handlers активны и обработают токен когда он придет');
          log('ℹ️ Пользователь может перезапустить приложение для немедленного получения токена');
        }
      }
      
      log('Push notifications initialized successfully');
      return true;

    } catch (error) {
      consola.error(logPrefix, 'Failed to initialize push notifications:', error);
      isInitialized = false;
      return false;
    }
  };

  /**
   * Функция для удаления слушателей (вызывается при отключении push)
   */
  globalThis.removePushNotificationsListeners = async () => {
    await removeAllPushListeners();
  };

  log('iOS push notifications plugin ready');
});

